<template>
  <section
    data-analytics="live.like_screen"
    class="live-room"
    role="dialog"
    aria-modal="true"
    :aria-label="video.title"
    @dblclick.stop.prevent="handleRoomDoubleClick"
    @touchstart.passive="handleLiveSwipeStart"
    @touchmove.passive="handleLiveSwipeMove"
    @touchend="handleLiveSwipeEnd"
    @touchcancel="resetLiveSwipe"
  >
    <div class="live-room-backdrop" :style="backdropStyle" aria-hidden="true" />
    <img
      v-if="coverUrl"
      class="live-room-cover-probe"
      :src="coverUrl"
      alt=""
      aria-hidden="true"
      @error.once="handleCoverError"
    >
    <video
      ref="player"
      :class="['live-room-player', `fit-${fitMode}`]"
      :poster="resolvedCoverUrl"
      :muted="muted"
      playsinline
      webkit-playsinline
      autoplay
      @play="handlePlaybackStarted"
      @loadedmetadata="handleVideoMetadata"
      @transitionend="handlePlayerLayoutTransition"
      @error="handlePlayerError"
    />
    <div class="live-room-shade" />
    <transition-group
      name="live-screen-like"
      tag="span"
      class="live-screen-like-bursts"
      aria-hidden="true"
    >
      <span
        v-for="heart in screenLikeBursts"
        :key="heart.id"
        class="live-screen-like-heart"
        :style="{ left: `${heart.x}px`, top: `${heart.y}px`, '--tap-rotate': `${heart.rotate}deg` }"
      >♥</span>
    </transition-group>

    <header class="live-room-header">
      <button type="button" data-analytics="live.back" :aria-label="$t('liveRoom.back')" @click="$emit('close')">
        <i class="el-icon-arrow-left" />
      </button>
      <div class="live-room-author" data-analytics="live.host_actions" role="button" tabindex="0" :aria-label="$t('liveRoom.openUserActions', { name: displayHost.name })" @click="openHostActions" @keydown.enter.prevent="openHostActions">
        <platform-avatar :src="displayHost.avatar_url" :alt="displayHost.name" />
        <span><strong>{{ displayHost.name }}</strong><small>{{ $t("liveRoom.watching", { count: $compact(displayLiveHeat) }) }}</small></span>
      </div>
      <button type="button" class="live-viewer-trigger" data-analytics="live.viewers_open" :aria-label="$t('liveRoom.viewersTitle')" @click="openViewerPanel">
        <span class="live-viewer-stack" aria-hidden="true">
          <platform-avatar v-for="viewer in viewers.slice(0, 3)" :key="viewer.user_id" :src="viewer.avatar_url" alt="" />
          <i v-if="!viewers.length" class="el-icon-user" />
        </span>
        <small>{{ $compact(viewerTotal || currentViewers) }}</small>
      </button>
      <div class="live-header-tools" :aria-label="$t('liveRoom.actions')">
        <button type="button" data-analytics="live.mute_toggle" :aria-label="$t(muted ? 'liveRoom.muted' : 'liveRoom.unmuted')" @click="toggleMuted">
          <i :class="muted ? 'el-icon-turn-off-microphone' : 'el-icon-microphone'" />
        </button>
        <button type="button" data-analytics="live.share" :aria-label="$t('dialog.share')" @click="$emit('share', video)">
          <i class="el-icon-share" />
        </button>
        <button type="button" data-analytics="live.quality_open" :aria-label="qualityButtonLabel" @click="qualityPanelVisible = true">
          <i class="el-icon-s-operation" />
        </button>
      </div>
    </header>

    <div v-if="loading || failed" class="live-room-state">
      <i :class="failed ? 'el-icon-warning-outline' : 'el-icon-loading'" />
      <strong>{{ $t(failed ? "liveRoom.failed" : "liveRoom.loading") }}</strong>
      <button v-if="failed" type="button" @click="connect">{{ $t("liveRoom.retry") }}</button>
    </div>

    <transition-group name="live-entrance-float" tag="div" class="live-entrance-events">
      <article
        v-for="event in visibleEntranceEvents"
        :key="event.id"
        :class="`theme-${event.theme || 'starlight'}`"
      >
        <img
          v-if="event.preview_url"
          class="live-entrance-art"
          :src="event.preview_url"
          alt=""
        >
        <span class="live-entrance-spark" aria-hidden="true"><i /><i /><i /></span>
        <avatar-with-frame class="live-entrance-avatar-shell" :size="44" :avatar="event.avatar_url" :avatar-alt="event.user_name" :frame-animation-url="event.avatar_frame_url" :frame-preview-url="event.avatar_frame_preview_url" />
        <p>
          <strong>{{ $t("decoration.liveEntered", { name: event.user_name }) }}</strong>
          <small>{{ entranceScreenName(event) }}</small>
        </p>
        <i class="el-icon-magic-stick" aria-hidden="true" />
      </article>
    </transition-group>

    <transition-group name="live-gift-pop" tag="div" class="live-gift-events">
      <article v-for="event in visibleGiftEvents" :key="event.id">
        <platform-avatar :src="event.avatar_url" :alt="event.user_name" />
        <p>{{ $t("liveRoom.sent", { name: event.user_name, gift: event.gift_name, count: $n(event.quantity) }) }}</p>
        <b class="live-gift-event-art"><img :src="event.icon" :alt="event.gift_name"></b>
      </article>
    </transition-group>

    <section class="live-chat-panel" :aria-label="$t('liveRoom.chatTitle')">
      <div ref="chatList" class="live-chat-list" aria-live="polite">
        <article v-for="message in chatMessages" :key="message.id" :class="{ own: message.is_owner, failed: message.delivery_state === 'failed', sending: message.delivery_state === 'sending' }">
          <button type="button" class="live-chat-avatar-shell" :aria-label="$t('liveRoom.openUserActions', { name: message.user_name })" @click="openUserActions(message)">
            <avatar-with-frame class="live-chat-avatar" :size="26" :avatar="message.avatar_url" :avatar-alt="message.user_name" :frame-animation-url="message.avatar_frame_url" :frame-preview-url="message.avatar_frame_preview_url" />
          </button>
          <p>
            <button type="button" class="live-chat-person" :aria-label="$t('liveRoom.openUserActions', { name: message.user_name })" @click="openUserActions(message)">
              <live-level-badge :level="message.level" :icon-url="message.level_icon_url" :title="message.level_title" compact /><strong>{{ message.user_name }}</strong>
            </button>
            <span>{{ message.content }}</span>
            <button v-if="message.is_owner && message.delivery_state === 'failed'" type="button" class="live-chat-retry" :aria-label="$t('common.retry')" @click="retryChat(message)"><i class="el-icon-refresh-right" /></button>
          </p>
        </article>
        <p v-if="chatInitialized && chatMessages.length === 0" class="live-chat-empty">
          {{ $t("liveRoom.chatEmpty") }}
        </p>
      </div>
    </section>

    <div class="live-chat-editor">
      <button v-if="!currentUser" type="button" class="live-chat-login" @click="$emit('login')">
        <i class="el-icon-chat-dot-round" /> {{ $t("liveRoom.chatLogin") }}
      </button>
      <template v-else>
        <button type="button" class="live-chat-mention-button" data-analytics="live.mention_open" :aria-label="$t('liveRoom.mention')" @click="toggleMentionPanel">@</button>
        <el-input
          v-model="chatText"
          :maxlength="150"
          :placeholder="$t('liveRoom.chatPlaceholder')"
          @input="handleChatInput"
          @focus="handleChatFocus"
          @blur="handleChatBlur"
          @keydown.enter.native.prevent="handleChatEnter"
        />
        <button type="button" data-analytics="live.chat_send" :disabled="!chatText.trim()" :aria-label="$t('liveRoom.chatSend')" @click="sendChat">
          <i class="el-icon-position" />
        </button>
      </template>
      <button type="button" class="live-chat-gift-button" data-analytics="live.gifts_open" :aria-label="$t('liveRoom.gifts')" @click="openGifts">
        <i class="el-icon-present" />
      </button>
      <button v-if="liveGameUrl" type="button" class="live-chat-game-button" data-analytics="live.game_open" :aria-label="$t('liveRoom.gameOpen')" @click="openGame">
        <i class="el-icon-s-platform" />
      </button>
      <transition name="live-mention-pop">
        <section v-if="mentionVisible && currentUser" class="live-mention-panel" :aria-label="$t('liveRoom.mentionList')">
          <p v-if="viewersLoading"><i class="el-icon-loading" /> {{ $t("common.loading") }}</p>
          <button v-for="person in mentionCandidates" :key="`${person.is_host ? 'host' : 'viewer'}-${person.user_id}`" type="button" @click="insertMention(person)">
            <platform-avatar :src="person.avatar_url" :alt="person.name" />
            <span><span class="live-person-name"><live-level-badge :level="person.level" :icon-url="person.level_icon_url" :title="person.level_title" compact /><strong>{{ person.name }}</strong></span><small>{{ $t(person.is_host ? "liveRoom.host" : "liveRoom.paidCoins", { count: $n(person.paid_coins || 0) }) }}</small></span>
            <em v-if="person.is_host">{{ $t("liveRoom.pinned") }}</em>
          </button>
          <p v-if="!viewersLoading && !mentionCandidates.length">{{ $t("liveRoom.viewersEmpty") }}</p>
        </section>
      </transition>
    </div>

    <transition name="live-gift-sheet">
      <div v-if="gameFrameMounted && liveGameUrl" v-show="gamePanelVisible" class="live-game-sheet" role="dialog" aria-modal="true" :aria-label="$t('liveRoom.gameTitle')" @click.self="closeGame">
        <section>
          <span ref="gameSafeAreaProbe" class="live-game-safe-area-probe" aria-hidden="true" />
          <button type="button" class="live-game-close" data-analytics="live.game_close" :aria-label="$t('liveRoom.gameClose')" @click="closeGame"><i class="el-icon-close" /></button>
          <div class="live-game-frame" :aria-busy="gameFrameLoading ? 'true' : 'false'">
            <iframe
              ref="gameFrame"
              :key="gameFrameKey"
              :src="liveGameUrl"
              :title="$t('liveRoom.gameFrameTitle')"
              sandbox="allow-scripts allow-same-origin allow-forms allow-pointer-lock"
              scrolling="no"
              referrerpolicy="strict-origin-when-cross-origin"
              @load="handleGameFrameLoad"
            />
            <p v-if="gameFrameLoading" class="live-game-state"><i class="el-icon-loading" aria-hidden="true" />{{ $t("liveRoom.gameLoading") }}</p>
            <p v-else-if="gameFrameFailed" class="live-game-state failed">
              <i class="el-icon-warning-outline" aria-hidden="true" />
              <span>{{ $t("liveRoom.gameFailed") }}</span>
              <button type="button" data-analytics="live.game_retry" @click="retryGame">{{ $t("liveRoom.gameRetry") }}</button>
            </p>
          </div>
        </section>
      </div>
    </transition>

    <transition name="live-gift-sheet">
      <div v-if="viewerPanelVisible" class="live-viewer-sheet" @click.self="viewerPanelVisible = false">
        <section>
          <header>
            <div><strong>{{ $t("liveRoom.viewersTitle") }}</strong><small>{{ $t("liveRoom.viewerRankTip") }}</small></div>
            <button type="button" :aria-label="$t('liveRoom.closeViewers')" @click="viewerPanelVisible = false"><i class="el-icon-close" /></button>
          </header>
          <p v-if="viewersLoading" class="live-viewer-state"><i class="el-icon-loading" /> {{ $t("common.loading") }}</p>
          <div v-else-if="viewers.length" class="live-viewer-list">
            <button v-for="(viewer, index) in viewers" :key="viewer.user_id" type="button" @click="openUserActions(viewer)">
              <b>{{ $n(index + 1) }}</b><platform-avatar :src="viewer.avatar_url" :alt="viewer.name" />
              <span><span class="live-person-name"><live-level-badge :level="viewer.level" :icon-url="viewer.level_icon_url" :title="viewer.level_title" compact /><strong>{{ viewer.name }}</strong></span><small>{{ $t("liveRoom.paidCoins", { count: $n(viewer.paid_coins) }) }}</small></span><i class="el-icon-arrow-right" />
            </button>
          </div>
          <p v-else class="live-viewer-state">{{ $t("liveRoom.viewersEmpty") }}</p>
        </section>
      </div>
    </transition>

    <transition name="live-gift-sheet">
      <div v-if="selectedRoomUser" class="live-user-sheet" @click.self="selectedRoomUser = null">
        <section>
          <header>
            <platform-avatar :src="selectedRoomUser.avatar_url" :alt="selectedRoomUser.name" />
            <span><span class="live-person-name"><live-level-badge :level="selectedRoomUser.level" :icon-url="selectedRoomUser.level_icon_url" :title="selectedRoomUser.level_title" /><strong>{{ selectedRoomUser.name }}</strong></span><small>{{ $t(selectedRoomUser.is_host ? "liveRoom.host" : "liveRoom.paidCoins", { count: $n(selectedRoomUser.paid_coins || 0) }) }}</small></span>
            <button type="button" :aria-label="$t('common.close')" @click="selectedRoomUser = null"><i class="el-icon-close" /></button>
          </header>
          <div>
            <button v-if="currentUser && !selectedRoomUser.is_self" type="button" data-analytics="live.user_mention" @click="mentionRoomUser"><strong>@</strong>{{ $t("liveRoom.mentionUser") }}</button>
            <button v-if="!selectedRoomUser.is_self && (currentUser || selectedRoomUser.author_id)" type="button" data-analytics="live.user_profile" @click="viewRoomUserProfile"><i class="el-icon-user" />{{ $t("liveRoom.viewProfile") }}</button>
            <button v-if="!selectedRoomUser.is_self" type="button" data-analytics="live.user_follow" :disabled="followUpdating" @click="toggleRoomFollow">
              <i :class="followUpdating ? 'el-icon-loading' : (selectedRoomUser.is_following ? 'el-icon-check' : 'el-icon-plus')" />
              {{ $t(selectedRoomUser.is_following ? "feed.followed" : "feed.follow") }}
            </button>
            <button v-if="!selectedRoomUser.is_self" type="button" data-analytics="live.user_message" @click="messageRoomUser"><i class="el-icon-chat-dot-round" />{{ $t("directMessage.messageUser") }}</button>
          </div>
        </section>
      </div>
    </transition>

    <transition name="live-gift-sheet">
      <div v-if="qualityPanelVisible" class="live-quality-sheet" @click.self="qualityPanelVisible = false">
        <section>
          <header>
            <div><strong>{{ $t("liveRoom.qualityTitle") }}</strong><small>{{ currentStreamSummary }}</small></div>
            <button type="button" :aria-label="$t('liveRoom.closeQualityPanel')" @click="qualityPanelVisible = false"><i class="el-icon-close" /></button>
          </header>
          <div class="live-quality-list">
            <button type="button" data-analytics="live.quality.auto" :class="{ active: selectedQuality === 'auto' }" @click="selectQuality('auto')">
              <span><strong>{{ $t("liveRoom.qualityAuto") }}</strong><small>{{ $t("liveRoom.qualityAutoTip") }}</small></span>
              <i v-if="selectedQuality === 'auto'" class="el-icon-check" />
            </button>
            <button
              v-for="quality in qualityOptions"
              :key="quality.quality"
              type="button"
              :class="{ active: selectedQuality === quality.quality }"
              :data-analytics="`live.quality.${quality.quality}`"
              @click="selectQuality(quality.quality)"
            >
              <span>
                <strong>{{ qualityLabel(quality) }}</strong>
                <small>{{ qualitySummary(quality) }}</small>
              </span>
              <i v-if="selectedQuality === quality.quality" class="el-icon-check" />
            </button>
          </div>
          <div class="live-display-modes">
            <strong>{{ $t("liveRoom.displayMode") }}</strong>
            <div>
              <button v-for="mode in displayModes" :key="mode" type="button" :data-analytics="`live.display_mode.${mode}`" :class="{ active: displayMode === mode }" @click="setDisplayMode(mode)">
                {{ $t(`liveRoom.display_${mode}`) }}
              </button>
            </div>
          </div>
        </section>
      </div>
    </transition>

    <transition name="live-gift-sheet">
      <div v-if="giftPanelVisible" class="live-gift-sheet" @click.self="giftPanelVisible = false">
        <section>
          <header>
            <div class="live-gift-heading">
              <strong>{{ $t("liveRoom.giftTitle") }}</strong>
              <button type="button" class="live-gift-balance" data-analytics="live.recharge_open" :aria-label="$t('liveRoom.balance', { count: $n(walletBalance) })" @click="openRecharge"><i class="el-icon-coin" aria-hidden="true" /><span>{{ $n(walletBalance) }}</span><i class="el-icon-arrow-right" aria-hidden="true" /></button>
            </div>
            <button type="button" :aria-label="$t('liveRoom.closePanel')" @click="giftPanelVisible = false"><i class="el-icon-close" /></button>
          </header>
          <div class="live-gift-content">
            <div v-if="gifts.length" class="live-gift-grid">
              <div
                v-for="gift in gifts"
                :key="gift.id"
                class="live-gift-item"
                :class="{ active: selectedGift && selectedGift.id === gift.id }"
              >
                <button
                  type="button"
                  :class="{ active: selectedGift && selectedGift.id === gift.id }"
                  :disabled="giftSending && giftSendQueue.length >= 12"
                  data-analytics="live.gift_select"
                  @click="selectGift(gift)"
                >
                  <b class="live-gift-art"><img :src="gift.icon" :alt="gift.name"></b><strong>{{ gift.name }}</strong><small>{{ $n(gift.coin_price) }}</small>
                </button>
                <button v-if="selectedGift && selectedGift.id === gift.id" type="button" class="live-gift-send live-gift-card-send" data-analytics="live.gift_send" :disabled="giftSending && giftSendQueue.length >= 12" @click="queueGift(gift)">
                  <i v-if="isGiftSending(gift)" class="el-icon-loading" /><span>{{ $t("liveRoom.sendCompact") }}</span><b v-if="selectedGift && selectedGift.id === gift.id && comboCount > 1" :key="comboCount" class="live-gift-combo-count">×{{ $n(comboCount) }}</b>
                </button>
              </div>
            </div>
            <p v-else class="live-gift-empty">{{ $t("liveRoom.emptyGift") }}</p>
          </div>
          <footer v-if="selectedGift">
            <label>{{ $t("liveRoom.quantity") }} <el-input-number v-model="quantity" :min="1" :max="99" size="small" /></label>
          </footer>
        </section>
      </div>
    </transition>
    <live-recharge-sheet
      :visible="rechargeVisible"
      :balance="walletBalance"
      @close="rechargeVisible = false"
      @recharged="handleRechargeSuccess"
    />
  </section>
</template>

<script>
import mpegts from "mpegts.js"
import {
  addLiveChat,
  addLiveEntrance,
  addLiveLikes,
  createLiveRoomWsTicket,
  getLiveChat,
  getLiveEntrances,
  getLiveGifts,
  getLiveLikes,
  getLiveStream,
  getLiveViewers,
  updateFollow,
  updateUserFollow,
  updateLivePresence
} from "../api/content"
import { addGift, getLiveGiftCatalog } from "../api/monetization"
import { API_BASE_URL } from "../config/api"
import {
  canStartSignedPlaybackUrl,
  isMpegtsHttp401,
  playbackReconnectDelay
} from "../utils/livePlayback"
import AvatarWithFrame from "./AvatarWithFrame.vue"
import LiveLevelBadge from "./LiveLevelBadge.vue"
import LiveRechargeSheet from "./LiveRechargeSheet.vue"

const GIFT_DISPLAY_MS = 4000
const ENTRANCE_DISPLAY_MS = 5200
const DISPLAY_MODE_KEY = "starlive_live_display_mode"
const GAME_VIEWPORT_CHANGE = "STARLIVE_VIEWPORT_CHANGE"
const GAME_VIEWPORT_REQUEST = "STARLIVE_VIEWPORT_REQUEST"
const GAME_VISIBILITY_CHANGE = "STARLIVE_VISIBILITY_CHANGE"
const GAME_VIEWPORT_VERSION = 1

export default {
  name: "LiveRoom",
  components: { AvatarWithFrame, LiveLevelBadge, LiveRechargeSheet },
  props: {
    video: { type: Object, required: true },
    currentUser: { type: Object, default: null }
  },
  data() {
    return {
      loading: true, failed: false, muted: false, coverFallbackActive: false, mpegtsPlayer: null, hlsPlayer: null,
      streamInfo: null, streamSyncTimer: null, streamSyncPolling: false, streamRefreshPromise: null,
      reconnectTimer: null, stablePlaybackTimer: null, reconnectAttempts: 0,
      playbackLifecycleGeneration: 0, connectRequestId: 0, activePlaybackUrl: "", rejectedPlaybackUrl: "",
      disposed: false, suppressPlayerErrors: false,
      selectedQuality: "auto", activeQualityCode: "", autoQualityOffset: 0, qualityPanelVisible: false,
      gamePanelVisible: false, gameFrameMounted: false, gameFrameLoading: false, gameFrameFailed: false, gameFrameKey: 0, gameLoadTimer: null,
      gameViewportObserver: null, gameViewportFrame: null, gameViewportRaf: null, gameViewportReason: "",
      displayMode: ["auto", "contain", "cover"].includes(localStorage.getItem(DISPLAY_MODE_KEY))
        ? localStorage.getItem(DISPLAY_MODE_KEY)
        : "auto",
      autoFitMode: "cover", mediaWidth: 0, mediaHeight: 0,
      pollTimer: null, lastGiftId: 0, giftEvents: [], giftDismissTimers: new Map(),
      giftPollInitialized: false, giftPolling: false,
      likePollTimer: null, lastLikeId: 0, likePollInitialized: false, likePolling: false,
      liveLikeCount: 0, pendingLikeQuantity: 0, activeLikeQuantity: 0,
      likeSending: false, likeSendTimer: null, likeBursts: [], likeBurstSequence: 0, likeBurstTimers: new Map(),
      screenLikeBursts: [], screenLikeSequence: 0, screenLikeTimers: new Map(),
      liveHeat: Number(this.video.stats && this.video.stats.heat || 0),
      currentViewers: Number(this.video.stats && this.video.stats.current_viewers || 0), liveHeatCalculatedAt: 0,
      presenceTimer: null, presenceActive: false,
      viewerTimer: null, viewers: [], viewerTotal: 0, viewersLoading: false, viewerPolling: false,
      viewerPanelVisible: false, mentionVisible: false, liveHost: null, selectedRoomUser: null, followUpdating: false,
      entrancePollTimer: null, lastEntranceId: 0, entranceEvents: [], entranceDismissTimers: new Map(),
      entrancePollInitialized: false, entrancePolling: false, ownEntranceAnnounced: false,
      chatTimer: null, chatMessages: [], lastChatId: 0, chatInitialized: false, chatPolling: false,
      roomSocket: null, roomSocketCursor: "0-0", roomSocketReady: false,
      roomConnectTimer: null, roomReconnectTimer: null, roomReconnectAttempts: 0,
      roomSignalTimer: null, roomPendingSignals: new Set(), roomFallbackTimer: null,
      chatText: "", chatFocusTimer: null,
      gifts: [], walletBalance: 0, giftPanelVisible: false, rechargeVisible: false,
      selectedGift: null, quantity: 1, giftSending: false,
      giftSendQueue: [], activeGiftBatch: null, giftQueueTimer: null,
      comboCount: 0, comboTimer: null,
      swipeTracking: false, swipeStartX: 0, swipeStartY: 0, swipeLastY: 0,
      swipeStartTime: 0, swipeMoved: false, suppressRoomClickUntil: 0,
      lastLiveTapTime: 0, lastLiveTapX: 0, lastLiveTapY: 0
    }
  },
  computed: {
    displayHost() {
      return this.liveHost || {
        name: this.video.author.name,
        avatar_url: this.video.author.avatar_url,
        level: this.video.author.level,
        level_title: this.video.author.level_title,
        level_icon_url: this.video.author.level_icon_url
      }
    },
    visibleGiftEvents() { return this.giftEvents.slice(-3) },
    visibleEntranceEvents() {
      return this.entranceEvents.filter(event => Boolean(String(event.preview_url || "").trim())).slice(-2)
    },
    qualityOptions() {
      const rows = this.streamInfo && Array.isArray(this.streamInfo.qualities) ? this.streamInfo.qualities : []
      const player = this.$refs.player
      const canFlv = Boolean(mpegts.isSupported() && mpegts.getFeatureList().mseLivePlayback)
      const canHls = Boolean(
        (player && player.canPlayType("application/vnd.apple.mpegurl"))
        || (window.Hls && window.Hls.isSupported())
      )
      // 只展示当前浏览器真正能播放的档位，避免 Safari 选择到只有 FLV 的清晰度。
      return rows.filter(item =>
        (canFlv && item.relay_path)
        || (canHls && item.relay_hls_path)
      )
    },
    activeVariant() {
      if (!this.qualityOptions.length) return null
      if (this.selectedQuality !== "auto") {
        return this.qualityOptions.find(item => item.quality === this.selectedQuality) || this.qualityOptions[0]
      }
      return this.qualityOptions[this.autoQualityIndex()] || this.qualityOptions[0]
    },
    qualityButtonLabel() {
      if (this.selectedQuality === "auto") return this.$t("liveRoom.qualityAuto")
      return this.activeVariant ? this.qualityLabel(this.activeVariant) : this.$t("liveRoom.quality")
    },
    fitMode() {
      return this.displayMode === "auto" ? this.autoFitMode : this.displayMode
    },
    coverUrl() {
      return String(this.video.cover_url || "").trim()
    },
    resolvedCoverUrl() {
      return this.coverFallbackActive || !this.coverUrl
        ? "/media/covers/starlive-live-default.png"
        : this.coverUrl
    },
    backdropStyle() {
      const url = this.resolvedCoverUrl.replace(/["\\\n\r]/g, "")
      return url ? { backgroundImage: `url("${url}")` } : {}
    },
    currentStreamSummary() {
      if (!this.activeVariant) return this.$t("liveRoom.metadataPending")
      return this.qualitySummary({
        ...this.activeVariant,
        width: this.mediaWidth || this.activeVariant.width,
        height: this.mediaHeight || this.activeVariant.height
      })
    },
    displayModes() {
      return ["auto", "contain", "cover"]
    },
    liveGameUrl() {
      const value = this.streamInfo
        ? this.streamInfo.game_url
        : this.video.live_game_url
      return /^https:\/\/[^\s]+$/i.test(String(value || "").trim()) ? String(value).trim() : ""
    },
    giftReservedCoins() {
      const queued = this.giftSendQueue.reduce((total, batch) => total + Number(batch.coinAmount || 0), 0)
      return queued + Number(this.activeGiftBatch && this.activeGiftBatch.coinAmount || 0)
    },
    spendableGiftBalance() {
      return Math.max(0, Number(this.walletBalance || 0) - this.giftReservedCoins)
    },
    displayLiveLikeCount() {
      return Number(this.liveLikeCount || 0) + this.pendingLikeQuantity + this.activeLikeQuantity
    },
    displayLiveHeat() {
      return Math.max(0, Number(this.liveHeat || 0))
    },
    mentionCandidates() {
      const candidates = []
      if (this.liveHost) candidates.push({ ...this.liveHost, is_host: true })
      this.viewers.forEach(viewer => {
        if (!candidates.some(item => Number(item.user_id) > 0 && Number(item.user_id) === Number(viewer.user_id))) {
          candidates.push(viewer)
        }
      })
      const match = this.chatText.match(/@([^@\s]*)$/u)
      const keyword = match ? String(match[1] || "").trim().toLocaleLowerCase() : ""
      return (keyword ? candidates.filter(item => String(item.name || "").toLocaleLowerCase().includes(keyword)) : candidates).slice(0, 20)
    }
  },
  watch: {
    video(nextVideo, previousVideo) {
      if (nextVideo !== previousVideo) this.coverFallbackActive = false
      if (nextVideo !== previousVideo) this.destroyGame()
    },
    liveGameUrl(value, previousValue) {
      if (!value || (previousValue && value !== previousValue)) this.destroyGame()
    },
    // 游客在直播间内完成登录后，也要立即匹配并播放本人当前佩戴的入场飘屏。
    currentUser(value) {
      if (value && !this.ownEntranceAnnounced) this.announceOwnEntrance()
      if (value) this.loadShop()
      else {
        this.gifts = []
        this.selectedGift = null
        this.walletBalance = 0
      }
      this.initializeChat()
      this.loadViewers(true)
    },
    giftPanelVisible() {
      // 礼物栏会改变播放器实际比例，必须重新计算 contain/cover，避免沿用全屏裁切造成主体看起来下移。
      this.$nextTick(() => {
        window.requestAnimationFrame(this.handleVideoMetadata)
      })
    }
  },
  mounted() {
    this.connect()
    this.pollStreamSource()
    if (this.currentUser) this.loadShop()
    this.initializeRoomRealtime()
    this.loadViewers(true)
    window.addEventListener("starlive:viewport-change", this.handleLiveViewportChange)
    document.addEventListener("visibilitychange", this.handlePresenceVisibility)
  },
  beforeDestroy() {
    this.stopPresence(true)
    this.disposed = true
    this.playbackLifecycleGeneration += 1
    this.connectRequestId += 1
    this.streamRefreshPromise = null
    this.destroyPlayer()
    window.clearTimeout(this.pollTimer)
    window.clearTimeout(this.reconnectTimer)
    window.clearTimeout(this.streamSyncTimer)
    window.clearTimeout(this.stablePlaybackTimer)
    window.clearTimeout(this.entrancePollTimer)
    window.clearTimeout(this.chatTimer)
    window.clearTimeout(this.chatFocusTimer)
    window.clearTimeout(this.likePollTimer)
    window.clearTimeout(this.likeSendTimer)
    window.clearTimeout(this.giftQueueTimer)
    window.clearTimeout(this.comboTimer)
    window.clearTimeout(this.viewerTimer)
    window.clearTimeout(this.roomReconnectTimer)
    window.clearTimeout(this.roomConnectTimer)
    window.clearTimeout(this.roomSignalTimer)
    window.clearTimeout(this.roomFallbackTimer)
    this.disconnectRoomRealtime(false)
    this.destroyGame()
    window.removeEventListener("starlive:viewport-change", this.handleLiveViewportChange)
    document.removeEventListener("visibilitychange", this.handlePresenceVisibility)
    this.giftDismissTimers.forEach(timer => window.clearTimeout(timer))
    this.giftDismissTimers.clear()
    this.entranceDismissTimers.forEach(timer => window.clearTimeout(timer))
    this.entranceDismissTimers.clear()
    this.likeBurstTimers.forEach(timer => window.clearTimeout(timer))
    this.likeBurstTimers.clear()
    this.screenLikeTimers.forEach(timer => window.clearTimeout(timer))
    this.screenLikeTimers.clear()
    if (document.body) document.body.classList.remove("live-chat-focused")
  },
  methods: {
    roomWebSocketUrl(path) {
      const api = new URL(API_BASE_URL, window.location.origin)
      const scheme = api.protocol === "https:" ? "wss:" : "ws:"
      return `${scheme}//${api.host}${path}`
    },
    async initializeRoomRealtime() {
      let ticket = null
      try {
        const response = await createLiveRoomWsTicket({ live_id: this.video.id })
        ticket = response.data || null
        if (ticket && this.roomSocketCursor === "0-0") {
          this.roomSocketCursor = String(ticket.stream_id || "0-0")
        }
      } catch (error) {
        if (error.responseData && Number(error.responseData.code) === 410) this.$emit("ended")
      }
      await Promise.all([
        this.pollGifts(),
        this.initializeLiveLikes(),
        this.initializeEntrances(),
        this.initializeChat()
      ].map(task => Promise.resolve(task).catch(() => {})))
      if (this.disposed || document.hidden) return
      if (ticket) this.openRoomRealtime(ticket)
      else {
        this.startRoomRealtimeFallback()
        this.scheduleRoomRealtimeReconnect()
      }
    },
    async connectRoomRealtime() {
      if (this.disposed || document.hidden || this.roomSocket) return
      try {
        const response = await createLiveRoomWsTicket({ live_id: this.video.id })
        if (this.disposed || document.hidden) return
        this.openRoomRealtime(response.data || {})
      } catch (error) {
        if (error.responseData && Number(error.responseData.code) === 410) {
          this.$emit("ended")
          return
        }
        this.startRoomRealtimeFallback()
        this.scheduleRoomRealtimeReconnect()
      }
    },
    openRoomRealtime(ticket) {
      const path = String(ticket.websocket_path || "")
      const protocols = Array.isArray(ticket.subprotocols) ? ticket.subprotocols.filter(Boolean) : []
      if (!path.startsWith("/ws/") || protocols.length !== 2 || this.roomSocket) {
        this.startRoomRealtimeFallback()
        this.scheduleRoomRealtimeReconnect()
        return
      }
      let socket
      try {
        socket = new WebSocket(this.roomWebSocketUrl(path), protocols)
      } catch (_error) {
        this.startRoomRealtimeFallback()
        this.scheduleRoomRealtimeReconnect()
        return
      }
      this.roomSocket = socket
      this.roomSocketReady = false
      this.roomConnectTimer = window.setTimeout(() => {
        if (this.roomSocket === socket && !this.roomSocketReady) socket.close()
      }, 8000)
      socket.onopen = () => {
        socket.send(JSON.stringify({ action: "subscribe", last_stream_id: this.roomSocketCursor || "0-0" }))
      }
      socket.onmessage = event => {
        let message
        try {
          message = JSON.parse(String(event.data || "{}"))
        } catch (_error) {
          return
        }
        if (message.event === "ready") {
          window.clearTimeout(this.roomConnectTimer)
          this.roomConnectTimer = null
          this.roomSocketReady = true
          this.roomReconnectAttempts = 0
          this.stopRoomRealtimeFallback()
          return
        }
        if (message.event !== "room_event" || Number(message.live_id) !== Number(this.video.id)) return
        this.roomSocketCursor = String(message.stream_id || this.roomSocketCursor || "0-0")
        const signal = message.payload && message.payload.event
        if (["chat", "gift", "like", "entrance"].includes(signal)) this.queueRoomRealtimeSignal(signal)
      }
      socket.onerror = () => socket.close()
      socket.onclose = () => {
        window.clearTimeout(this.roomConnectTimer)
        this.roomConnectTimer = null
        if (this.roomSocket === socket) this.roomSocket = null
        this.roomSocketReady = false
        if (!this.disposed && !document.hidden) {
          this.startRoomRealtimeFallback()
          this.scheduleRoomRealtimeReconnect()
        }
      }
    },
    disconnectRoomRealtime(reconnect = false) {
      window.clearTimeout(this.roomReconnectTimer)
      this.roomReconnectTimer = null
      window.clearTimeout(this.roomConnectTimer)
      this.roomConnectTimer = null
      const socket = this.roomSocket
      this.roomSocket = null
      this.roomSocketReady = false
      if (socket) {
        socket.onclose = null
        socket.onerror = null
        socket.close()
      }
      if (reconnect && !this.disposed && !document.hidden) this.scheduleRoomRealtimeReconnect()
    },
    scheduleRoomRealtimeReconnect() {
      if (this.disposed || document.hidden || this.roomReconnectTimer || this.roomSocket) return
      this.roomReconnectAttempts += 1
      const delay = Math.min(1000 * (2 ** Math.min(4, this.roomReconnectAttempts - 1)), 15000)
      this.roomReconnectTimer = window.setTimeout(() => {
        this.roomReconnectTimer = null
        this.connectRoomRealtime()
      }, delay)
    },
    queueRoomRealtimeSignal(signal) {
      this.roomPendingSignals.add(signal)
      if (this.roomSignalTimer) return
      this.roomSignalTimer = window.setTimeout(() => {
        this.roomSignalTimer = null
        const signals = Array.from(this.roomPendingSignals)
        this.roomPendingSignals.clear()
        this.syncRoomRealtimeSignals(signals)
      }, 80)
    },
    syncRoomRealtimeSignals(signals = ["chat", "gift", "like", "entrance"]) {
      const tasks = []
      if (signals.includes("chat")) tasks.push(this.drainRoomRealtime(this.pollChat, 50))
      if (signals.includes("gift")) tasks.push(this.drainRoomRealtime(this.pollGifts, 30))
      if (signals.includes("like")) tasks.push(this.drainRoomRealtime(this.pollLiveLikes, 50))
      if (signals.includes("entrance")) tasks.push(this.drainRoomRealtime(this.pollEntrances, 30))
      return Promise.all(tasks.map(task => Promise.resolve(task).catch(() => {})))
    },
    drainRoomRealtime(loader, pageSize, depth = 0) {
      return Promise.resolve(loader.call(this)).then(count => {
        if (Number(count || 0) < pageSize || depth >= 9 || this.disposed) return count
        return this.drainRoomRealtime(loader, pageSize, depth + 1)
      })
    },
    startRoomRealtimeFallback() {
      if (this.disposed || document.hidden || this.roomFallbackTimer || this.roomSocketReady) return
      const run = () => {
        this.roomFallbackTimer = null
        if (this.disposed || document.hidden || this.roomSocketReady) return
        this.syncRoomRealtimeSignals().finally(() => {
          if (!this.disposed && !document.hidden && !this.roomSocketReady) {
            this.roomFallbackTimer = window.setTimeout(run, 5000)
          }
        })
      }
      this.roomFallbackTimer = window.setTimeout(run, 1000)
    },
    stopRoomRealtimeFallback() {
      window.clearTimeout(this.roomFallbackTimer)
      this.roomFallbackTimer = null
    },
    openGame() {
      if (!this.liveGameUrl) return
      const shouldMount = !this.gameFrameMounted
      this.giftPanelVisible = false
      this.qualityPanelVisible = false
      this.viewerPanelVisible = false
      this.selectedRoomUser = null
      this.mentionVisible = false
      this.gamePanelVisible = true
      if (shouldMount) {
        this.gameFrameMounted = true
        this.startGameFrameLoad()
      }
      this.$nextTick(() => {
        this.setupGameViewportBridge()
        this.sendGameVisibility(true)
        this.scheduleGameViewportSync(shouldMount ? "panel-first-open" : "panel-reopen")
      })
    },
    closeGame() {
      this.sendGameVisibility(false)
      this.teardownGameViewportBridge()
      this.gamePanelVisible = false
    },
    destroyGame() {
      this.sendGameVisibility(false)
      this.teardownGameViewportBridge()
      this.gamePanelVisible = false
      this.gameFrameMounted = false
      this.gameFrameLoading = false
      this.gameFrameFailed = false
      window.clearTimeout(this.gameLoadTimer)
      this.gameLoadTimer = null
    },
    startGameFrameLoad() {
      window.clearTimeout(this.gameLoadTimer)
      this.gameFrameLoading = true
      this.gameFrameFailed = false
      this.gameLoadTimer = window.setTimeout(() => {
        this.gameFrameLoading = false
        this.gameFrameFailed = true
      }, 12000)
    },
    handleGameFrameLoad() {
      window.clearTimeout(this.gameLoadTimer)
      this.gameLoadTimer = null
      this.gameFrameLoading = false
      this.gameFrameFailed = false
      if (this.gamePanelVisible) {
        this.setupGameViewportBridge()
        this.sendGameVisibility(true)
        this.scheduleGameViewportSync("iframe-load")
      } else {
        this.sendGameVisibility(false)
      }
    },
    retryGame() {
      this.teardownGameViewportBridge()
      this.gameFrameKey += 1
      this.startGameFrameLoad()
      this.$nextTick(() => this.setupGameViewportBridge())
    },
    gameViewportOrigin() {
      try {
        const url = new URL(this.liveGameUrl)
        return url.protocol === "https:" ? url.origin : ""
      } catch (error) {
        return ""
      }
    },
    sendGameVisibility(visible) {
      const frame = this.$refs.gameFrame
      const origin = this.gameViewportOrigin()
      if (!frame || !frame.contentWindow || !origin) return
      frame.contentWindow.postMessage({
        type: GAME_VISIBILITY_CHANGE,
        version: GAME_VIEWPORT_VERSION,
        visible: Boolean(visible)
      }, origin)
    },
    setupGameViewportBridge() {
      if (!this.gamePanelVisible || !this.liveGameUrl) return
      const frame = this.$refs.gameFrame
      if (!frame || this.gameViewportFrame === frame) return
      this.teardownGameViewportBridge()
      this.gameViewportFrame = frame
      window.addEventListener("message", this.handleGameViewportMessage)
      window.addEventListener("resize", this.handleGameViewportResize, { passive: true })
      window.addEventListener("orientationchange", this.handleGameViewportOrientation, { passive: true })
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", this.handleGameVisualViewportResize, { passive: true })
      }
      if (typeof ResizeObserver !== "undefined") {
        this.gameViewportObserver = new ResizeObserver(() => this.scheduleGameViewportSync("panel-resize"))
        this.gameViewportObserver.observe(frame)
      }
      this.scheduleGameViewportSync("panel-open")
    },
    teardownGameViewportBridge() {
      if (this.gameViewportObserver) this.gameViewportObserver.disconnect()
      this.gameViewportObserver = null
      this.gameViewportFrame = null
      if (this.gameViewportRaf !== null) window.cancelAnimationFrame(this.gameViewportRaf)
      this.gameViewportRaf = null
      this.gameViewportReason = ""
      window.removeEventListener("message", this.handleGameViewportMessage)
      window.removeEventListener("resize", this.handleGameViewportResize)
      window.removeEventListener("orientationchange", this.handleGameViewportOrientation)
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", this.handleGameVisualViewportResize)
      }
    },
    handleGameViewportResize() {
      this.scheduleGameViewportSync("window-resize")
    },
    handleGameViewportOrientation() {
      this.scheduleGameViewportSync("orientationchange")
    },
    handleGameVisualViewportResize() {
      this.scheduleGameViewportSync("visual-viewport-resize")
    },
    handleGameViewportMessage(event) {
      const frame = this.$refs.gameFrame
      const origin = this.gameViewportOrigin()
      const data = event && event.data
      if (!frame || !origin || event.source !== frame.contentWindow || event.origin !== origin) return
      if (!data || data.type !== GAME_VIEWPORT_REQUEST || Number(data.version || GAME_VIEWPORT_VERSION) !== GAME_VIEWPORT_VERSION) return
      this.scheduleGameViewportSync("game-request")
    },
    scheduleGameViewportSync(reason) {
      if (!this.gamePanelVisible) return
      this.gameViewportReason = reason
      if (this.gameViewportRaf !== null) return
      this.gameViewportRaf = window.requestAnimationFrame(() => {
        this.gameViewportRaf = null
        const nextReason = this.gameViewportReason || "viewport-change"
        this.gameViewportReason = ""
        this.sendGameViewport(nextReason)
      })
    },
    gameSafeArea(width, height) {
      const probe = this.$refs.gameSafeAreaProbe
      const styles = probe ? window.getComputedStyle(probe) : null
      const inset = side => Math.max(0, Math.round(parseFloat(styles ? styles.getPropertyValue(`padding-${side}`) : "0") || 0))
      const left = Math.min(width, inset("left"))
      const right = Math.max(left, width - Math.min(width, inset("right")))
      const top = 0
      const bottom = Math.max(top, height - Math.min(height, inset("bottom")))
      return { left, top, right, bottom, width: right - left, height: bottom - top }
    },
    sendGameViewport(reason) {
      const frame = this.$refs.gameFrame
      const origin = this.gameViewportOrigin()
      if (!frame || !frame.contentWindow || !origin || !this.gamePanelVisible) return
      const rect = frame.getBoundingClientRect()
      const width = Math.max(0, Math.round(rect.width))
      const height = Math.max(0, Math.round(rect.height))
      if (!width || !height) return
      const safeArea = this.gameSafeArea(width, height)
      frame.contentWindow.postMessage({
        type: GAME_VIEWPORT_CHANGE,
        version: GAME_VIEWPORT_VERSION,
        displayMode: "half",
        windowWidth: width,
        windowHeight: height,
        pixelRatio: Number(window.devicePixelRatio || 1),
        safeArea,
        reason
      }, origin)
    },
    async loadViewers(initial = false) {
      if (this.disposed || this.viewerPolling) return
      window.clearTimeout(this.viewerTimer)
      this.viewerPolling = true
      if (initial && !this.viewers.length) this.viewersLoading = true
      try {
        const response = await getLiveViewers({ live_id: this.video.id, page: 1, limit: 30 })
        this.viewers = Array.isArray(response.data.list) ? response.data.list : []
        this.viewerTotal = Number(response.data.total || 0)
        this.liveHost = response.data.host || {
          author_id: Number(this.video.author.id || 0),
          user_id: Number(this.video.author.user_id || 0),
          name: this.video.author.name,
          avatar_url: this.video.author.avatar_url,
          level: this.video.author.level,
          level_title: this.video.author.level_title,
          level_icon_url: this.video.author.level_icon_url,
          is_host: true,
          paid_coins: 0
        }
      } catch (error) {
        if (error.responseData && Number(error.responseData.code) === 410) this.$emit("ended")
      } finally {
        this.viewerPolling = false
        this.viewersLoading = false
        if (!this.disposed) this.viewerTimer = window.setTimeout(() => this.loadViewers(false), document.hidden ? 15000 : 7000)
      }
    },
    openViewerPanel() {
      this.viewerPanelVisible = true
      this.mentionVisible = false
      this.loadViewers(true)
    },
    openUserActions(person) {
      if (!person) return
      const viewer = this.viewers.find(item => Number(item.user_id) === Number(person.user_id))
      const selected = { ...(viewer || person), name: person.name || person.user_name || (viewer && viewer.name) || "" }
      if (selected.is_self || (this.currentUser && Number(selected.user_id) === Number(this.currentUser.id))) return
      this.selectedRoomUser = selected
      this.viewerPanelVisible = false
      this.mentionVisible = false
    },
    openHostActions() {
      this.openUserActions(this.liveHost || {
        author_id: Number(this.video.author.id || 0),
        user_id: Number(this.video.author.user_id || 0),
        name: this.video.author.name,
        avatar_url: this.video.author.avatar_url,
        level: this.video.author.level,
        level_title: this.video.author.level_title,
        level_icon_url: this.video.author.level_icon_url,
        is_host: true,
        paid_coins: 0,
        is_self: Boolean(this.video.author.is_self),
        is_following: Boolean(this.video.author.followed)
      })
    },
    async toggleRoomFollow() {
      const person = this.selectedRoomUser
      if (!person || person.is_self || this.followUpdating) return
      this.followUpdating = true
      try {
        const response = person.author_id
          ? await updateFollow({ author_id: Number(person.author_id), status: person.is_following ? 0 : 1 })
          : await updateUserFollow({ target_user_id: Number(person.user_id), status: person.is_following ? 0 : 1 })
        const followed = Boolean(response.data.followed)
        this.$set(person, "is_following", followed)
        this.viewers.filter(item => person.author_id
          ? Number(item.author_id) === Number(person.author_id)
          : Number(item.user_id) === Number(person.user_id)
        ).forEach(item => this.$set(item, "is_following", followed))
        if (this.liveHost && person.author_id && Number(this.liveHost.author_id) === Number(person.author_id)) this.$set(this.liveHost, "is_following", followed)
      } finally {
        this.followUpdating = false
      }
    },
    messageRoomUser() {
      const person = this.selectedRoomUser
      if (!person || person.is_self) return
      const target = Number(person.user_id) > 0
        ? { target_type: "user", target_id: Number(person.user_id) }
        : { target_type: "author", target_id: Number(person.author_id) }
      this.selectedRoomUser = null
      this.$emit("message-user", target)
    },
    mentionRoomUser() {
      if (!this.selectedRoomUser) return
      this.insertMention(this.selectedRoomUser)
      this.selectedRoomUser = null
    },
    viewRoomUserProfile() {
      const person = this.selectedRoomUser
      if (!person || person.is_self) return
      this.selectedRoomUser = null
      this.$emit("profile-user", {
        user_id: Number(person.user_id || 0),
        author_id: Number(person.author_id || 0)
      })
    },
    toggleMentionPanel() {
      this.mentionVisible = !this.mentionVisible
      if (this.mentionVisible) this.loadViewers(true)
    },
    handleChatInput(value) {
      this.mentionVisible = /@[^@\s]*$/u.test(String(value || ""))
    },
    handleChatFocus() {
      window.clearTimeout(this.chatFocusTimer)
      if (document.body) document.body.classList.add("live-chat-focused")
      if (!document.body || !document.body.classList.contains("ios-device")) return
      const settle = () => {
        if (document.scrollingElement) document.scrollingElement.scrollTop = 0
        window.scrollTo(0, 0)
      }
      window.requestAnimationFrame(settle)
      this.chatFocusTimer = window.setTimeout(settle, 180)
    },
    handleChatBlur() {
      window.clearTimeout(this.chatFocusTimer)
      this.chatFocusTimer = window.setTimeout(() => {
        if (document.body) document.body.classList.remove("live-chat-focused")
      }, 120)
    },
    insertMention(person) {
      const name = String(person && (person.name || person.user_name) || "").trim()
      if (!name) return
      const source = String(this.chatText || "")
      this.chatText = /@[^@\s]*$/u.test(source)
        ? source.replace(/@[^@\s]*$/u, `@${name} `)
        : `${source}${source && !/\s$/u.test(source) ? " " : ""}@${name} `
      this.mentionVisible = false
      this.$nextTick(() => {
        const input = this.$el.querySelector(".live-chat-editor input")
        if (input) input.focus()
      })
    },
    entranceScreenName(event) {
      if (event.screen_name) return event.screen_name
      if (event.screen_code === "level_entrance") {
        return this.$t("decoration.levelEntrance", { level: this.$n(Number(event.level || 0)) })
      }
      return this.$t("decoration.defaultEntrance")
    },
    playbackFlvUrl(stream) {
      if (stream && stream.playback_mode === "direct") return String(stream.flv_url || "")
      if (!stream || !stream.relay_path) return ""
      return `${window.location.protocol}//${window.location.hostname}:${Number(stream.relay_port || 8101)}${stream.relay_path}`
    },
    playbackHlsUrl(stream) {
      if (stream && stream.playback_mode === "direct") return String(stream.hls_url || "")
      if (!stream || !stream.relay_hls_path) return ""
      return `${window.location.protocol}//${window.location.hostname}:${Number(stream.relay_port || 8101)}${stream.relay_hls_path}`
    },
    hlsFallbackUrl(stream) {
      const path = String(stream && stream.hls_fallback_path || "")
      if (!path || !path.startsWith("/live-hls/")) return ""
      return `${window.location.origin}${path}`
    },
    canStartPlaybackUrl(url, now = Date.now()) {
      return canStartSignedPlaybackUrl(url, this.rejectedPlaybackUrl, now, window.location.origin)
    },
    refreshStream() {
      if (this.streamRefreshPromise) return this.streamRefreshPromise
      const lifecycleGeneration = this.playbackLifecycleGeneration
      const liveId = Number(this.video.id)
      const refreshPromise = getLiveStream({ id: liveId })
        .then(response => {
          if (this.disposed || lifecycleGeneration !== this.playbackLifecycleGeneration) {
            const staleError = new Error("STALE_LIVE_RESULT")
            staleError.code = "STALE_LIVE_RESULT"
            throw staleError
          }
          if (liveId !== Number(this.video.id)) {
            const staleError = new Error("STALE_LIVE_RESULT")
            staleError.code = "STALE_LIVE_RESULT"
            throw staleError
          }
          return response.data || {}
        })
        .finally(() => {
          if (this.streamRefreshPromise === refreshPromise) this.streamRefreshPromise = null
        })
      this.streamRefreshPromise = refreshPromise
      return refreshPromise
    },
    async connect(resetAttempts = true) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
      if (resetAttempts) this.reconnectAttempts = 0
      const lifecycleGeneration = this.playbackLifecycleGeneration
      const requestId = ++this.connectRequestId
      this.destroyPlayer()
      this.loading = true
      this.failed = false
      try {
        const stream = await this.refreshStream()
        if (this.disposed || lifecycleGeneration !== this.playbackLifecycleGeneration) return
        if (requestId !== this.connectRequestId) return
        this.streamInfo = stream
        this.liveLikeCount = Math.max(this.liveLikeCount, Number(stream.live_like_count || 0))
        this.applyLiveHeat(stream.heat)
        if (!this.startPlayback(stream)) throw new Error("fresh live stream unavailable")
      } catch (error) {
        if (error && error.code === "STALE_LIVE_RESULT") return
        if (this.disposed || lifecycleGeneration !== this.playbackLifecycleGeneration) return
        if (requestId !== this.connectRequestId) return
        if (error.responseData && Number(error.responseData.code) === 410) {
          this.loading = false
          this.failed = false
          this.$emit("ended")
          return
        }
        this.scheduleReconnect()
      }
    },
    streamSessionFingerprint(stream) {
      if (!stream) return ""
      return `${String(stream.source || "")}:${String(stream.stream_session_id || "")}`
    },
    async pollStreamSource() {
      window.clearTimeout(this.streamSyncTimer)
      if (this.disposed || this.streamSyncPolling) return
      this.streamSyncPolling = true
      const lifecycleGeneration = this.playbackLifecycleGeneration
      try {
        const nextStream = await this.refreshStream()
        if (this.disposed || lifecycleGeneration !== this.playbackLifecycleGeneration) return
        const previousFingerprint = this.streamSessionFingerprint(this.streamInfo)
        const nextFingerprint = this.streamSessionFingerprint(nextStream)
        if (previousFingerprint && nextFingerprint && previousFingerprint !== nextFingerprint) {
          this.connectRequestId += 1
          this.streamInfo = nextStream
          this.selectedQuality = "auto"
          this.autoQualityOffset = 0
          this.reconnectAttempts = 0
          this.loading = true
          this.failed = false
          this.destroyPlayer()
          this.$nextTick(() => {
            if (!this.startPlayback(nextStream)) this.scheduleReconnect()
          })
        } else this.streamInfo = nextStream
        this.liveLikeCount = Math.max(this.liveLikeCount, Number(nextStream.live_like_count || 0))
        this.applyLiveHeat(nextStream.heat)
      } catch (error) {
        if (error && error.code === "STALE_LIVE_RESULT") return
        if (error.responseData && Number(error.responseData.code) === 410) this.$emit("ended")
      } finally {
        this.streamSyncPolling = false
        if (!this.disposed) this.streamSyncTimer = window.setTimeout(this.pollStreamSource, document.hidden ? 15000 : 5000)
      }
    },
    selectedPlaybackStream(stream) {
      const variant = this.activeVariant
      if (!variant) return stream
      return { ...stream, ...variant, relay_port: stream.relay_port }
    },
    startPlayback(stream) {
      const player = this.$refs.player
      if (!player || !stream) return false
      const playback = this.selectedPlaybackStream(stream)
      this.activeQualityCode = playback.quality || stream.default_quality || ""
      player.muted = this.muted
      const playbackURL = this.playbackFlvUrl(playback)
      if (this.canStartPlaybackUrl(playbackURL) && mpegts.isSupported() && mpegts.getFeatureList().mseLivePlayback) {
        return this.playFlv(playbackURL)
      }
      return this.playHls(playback)
    },
    autoQualityIndex() {
      const count = this.qualityOptions.length
      if (count <= 1) return 0
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
      const effectiveType = String(connection && connection.effectiveType || "")
      let base = 0
      if (connection && connection.saveData) base = count - 1
      else if (effectiveType === "slow-2g" || effectiveType === "2g") base = count - 1
      else if (effectiveType === "3g") base = Math.min(count - 1, Math.max(1, Math.floor(count / 2)))
      return Math.min(count - 1, base + this.autoQualityOffset)
    },
    qualityLabel(quality) {
      const group = ["original", "full_hd", "hd", "sd"].includes(quality.group) ? quality.group : "standard"
      const label = this.$t(`liveRoom.quality_${group}`)
      return this.$t("liveRoom.qualityVariant", { label, code: quality.quality })
    },
    qualitySummary(quality) {
      const parts = []
      if (Number(quality.width) > 0 && Number(quality.height) > 0) {
        parts.push(`${this.$n(quality.width)}×${this.$n(quality.height)}`)
      }
      if (Number(quality.bitrate_kbps) > 0) {
        parts.push(this.$t("liveRoom.bitrate", { value: this.$n(quality.bitrate_kbps) }))
      }
      if (Number(quality.fps) > 0) {
        parts.push(this.$t("liveRoom.frameRate", { value: this.$n(quality.fps) }))
      }
      if (quality.codec) parts.push(String(quality.codec))
      return parts.join(" · ") || this.$t("liveRoom.metadataPending")
    },
    selectQuality(quality) {
      if (quality === this.selectedQuality) {
        this.qualityPanelVisible = false
        return
      }
      this.selectedQuality = quality
      this.autoQualityOffset = 0
      this.qualityPanelVisible = false
      this.loading = true
      this.failed = false
      this.destroyPlayer()
      this.$nextTick(() => {
        if (!this.startPlayback(this.streamInfo)) this.scheduleReconnect()
      })
    },
    setDisplayMode(mode) {
      if (!this.displayModes.includes(mode)) return
      this.displayMode = mode
      localStorage.setItem(DISPLAY_MODE_KEY, mode)
    },
    handleVideoMetadata() {
      const player = this.$refs.player
      if (!player) return
      this.mediaWidth = Number(player.videoWidth || 0)
      this.mediaHeight = Number(player.videoHeight || 0)
      if (!this.mediaWidth || !this.mediaHeight) return
      const streamRatio = this.mediaWidth / this.mediaHeight
      const viewportRatio = Math.max(1, player.clientWidth) / Math.max(1, player.clientHeight)
      if (streamRatio <= 1 && viewportRatio <= 1) {
        this.autoFitMode = "cover"
        return
      }
      const ratioDifference = Math.max(streamRatio / viewportRatio, viewportRatio / streamRatio)
      const sameOrientation = (streamRatio <= 1 && viewportRatio <= 1)
        || (streamRatio > 1 && viewportRatio > 1)
      this.autoFitMode = sameOrientation && ratioDifference <= 1.18 ? "cover" : "contain"
    },
    handleLiveViewportChange() {
      if (this.disposed) return
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          if (!this.disposed) this.handleVideoMetadata()
        })
      })
    },
    handlePlayerLayoutTransition(event) {
      if (!event || event.propertyName === "height") this.handleVideoMetadata()
    },
    playFlv(url) {
      const player = this.$refs.player
      if (!player || !url) return false
      const usesManagedMediaSource = typeof window.ManagedMediaSource !== "undefined"
      player.playsInline = true
      player.disableRemotePlayback = true
      this.activePlaybackUrl = url
      this.mpegtsPlayer = mpegts.createPlayer(
        { type: "flv", isLive: true, url, cors: true },
        {
          enableWorker: !usesManagedMediaSource,
          enableWorkerForMSE: false,
          enableStashBuffer: true,
          stashInitialSize: 384 * 1024,
          lazyLoad: false,
          autoCleanupSourceBuffer: true,
          autoCleanupMaxBackwardDuration: 30,
          autoCleanupMinBackwardDuration: 15
        }
      )
      this.mpegtsPlayer.attachMediaElement(player)
      this.mpegtsPlayer.on(mpegts.Events.ERROR, this.handlePlayerError)
      this.mpegtsPlayer.load()
      player.play().catch(() => {})
      return true
    },
    playHls(stream) {
      const player = this.$refs.player
      if (!player) return false
      const nativeHls = Boolean(player.canPlayType("application/vnd.apple.mpegurl"))
      // Safari/iPhone 使用同源的服务端 HLS 降级，避免主播端 HLS CDN 节点不可达；
      // 其他浏览器仍优先使用主播端上报的真实地址。
      const url = nativeHls
        ? (this.hlsFallbackUrl(stream) || this.playbackHlsUrl(stream))
        : (this.playbackHlsUrl(stream) || this.hlsFallbackUrl(stream))
      if (!this.canStartPlaybackUrl(url)) return false
      this.activePlaybackUrl = url
      if (nativeHls) {
        player.src = url
        player.load()
        player.play().catch(() => {})
        return true
      }
      if (window.Hls && window.Hls.isSupported()) {
        this.hlsPlayer = new window.Hls({ enableWorker: true, lowLatencyMode: true, backBufferLength: 30 })
        this.hlsPlayer.loadSource(url)
        this.hlsPlayer.attachMedia(player)
        this.hlsPlayer.on(window.Hls.Events.MANIFEST_PARSED, () => player.play().catch(() => {}))
        this.hlsPlayer.on(window.Hls.Events.ERROR, (_event, data) => {
          if (data && data.fatal) this.handlePlayerError("hls", data.type, data.response || data)
        })
        return true
      }
      return false
    },
    destroyPlayer() {
      this.suppressPlayerErrors = true
      if (this.mpegtsPlayer) {
        this.mpegtsPlayer.off(mpegts.Events.ERROR, this.handlePlayerError)
        this.mpegtsPlayer.destroy()
        this.mpegtsPlayer = null
      }
      if (this.hlsPlayer) {
        this.hlsPlayer.destroy()
        this.hlsPlayer = null
      }
      const player = this.$refs.player
      if (player) {
        player.pause()
        player.removeAttribute("src")
        player.load()
      }
      this.activePlaybackUrl = ""
      this.$nextTick(() => { this.suppressPlayerErrors = false })
    },
    handlePlayerError(type, detail, info = {}) {
      if (this.disposed || this.suppressPlayerErrors) return
      if (isMpegtsHttp401(type, detail, info) && this.activePlaybackUrl) {
        this.rejectedPlaybackUrl = this.activePlaybackUrl
      }
      this.scheduleReconnect()
    },
    handleCoverError() {
      this.coverFallbackActive = true
    },
    handlePlaybackStarted() {
      this.handleVideoMetadata()
      this.loading = false
      this.failed = false
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
      window.clearTimeout(this.stablePlaybackTimer)
      // 只有连续播放达到稳定窗口才清零重连次数，避免短流反复 play/error 导致永远无法降级。
      this.stablePlaybackTimer = window.setTimeout(() => {
        this.reconnectAttempts = 0
      }, 8000)
      this.startPresence()
    },
    scheduleReconnect() {
      if (this.disposed || this.reconnectTimer) return
      this.stopPresence(true)
      window.clearTimeout(this.stablePlaybackTimer)
      this.stablePlaybackTimer = null
      this.reconnectAttempts += 1
      if (this.selectedQuality === "auto"
        && this.reconnectAttempts % 2 === 0
        && this.autoQualityOffset < this.qualityOptions.length - 1) {
        this.autoQualityOffset += 1
      }
      this.loading = true
      this.failed = false
      if (this.reconnectAttempts > 6) {
        this.destroyPlayer()
        const playback = this.streamInfo ? this.selectedPlaybackStream(this.streamInfo) : null
        if (playback && this.playHls(playback)) {
          this.reconnectAttempts = 0
          return
        }
        this.loading = false
        this.failed = true
        return
      }
      const delay = playbackReconnectDelay(this.reconnectAttempts)
      this.reconnectTimer = window.setTimeout(() => {
        this.reconnectTimer = null
        if (this.disposed) return
        // 播放失败后总是重新领取服务器保存的最新地址；正常播放期间的签名刷新
        // 只更新备用地址，不销毁当前播放器。
        this.connect(false)
      }, delay)
    },
    toggleMuted() {
      this.muted = !this.muted
      if (this.$refs.player) this.$refs.player.muted = this.muted
    },
    applyLiveHeat(heat) {
      if (!heat || typeof heat !== "object") return
      const calculatedAt = Date.parse(String(heat.calculated_at || "").replace(" ", "T") + (String(heat.calculated_at || "").includes("Z") ? "" : "Z")) || 0
      if (calculatedAt && this.liveHeatCalculatedAt && calculatedAt < this.liveHeatCalculatedAt) return
      const hasScore = Object.prototype.hasOwnProperty.call(heat, "score")
      const hasViewerCount = Object.prototype.hasOwnProperty.call(heat, "current_viewers")
      if (!hasScore && !hasViewerCount) return
      if (hasScore) this.liveHeat = Math.max(0, Number(heat.score))
      if (hasViewerCount) this.currentViewers = Math.max(0, Number(heat.current_viewers))
      if (calculatedAt) this.liveHeatCalculatedAt = calculatedAt
      if (this.video.stats) {
        this.video.stats.heat = this.liveHeat
        this.video.stats.current_viewers = this.currentViewers
      }
    },
    startPresence() {
      if (this.disposed || this.presenceActive || document.hidden) return
      this.presenceActive = true
      this.sendPresence("enter")
      this.schedulePresenceHeartbeat()
    },
    schedulePresenceHeartbeat() {
      window.clearTimeout(this.presenceTimer)
      if (!this.presenceActive || this.disposed) return
      this.presenceTimer = window.setTimeout(() => {
        if (!this.presenceActive || this.disposed) return
        if (document.hidden) {
          this.stopPresence(true)
          return
        }
        this.sendPresence("heartbeat")
        this.schedulePresenceHeartbeat()
      }, 15000)
    },
    stopPresence(sendLeave = false) {
      window.clearTimeout(this.presenceTimer)
      this.presenceTimer = null
      if (!this.presenceActive) return
      this.presenceActive = false
      if (sendLeave) this.sendPresence("leave")
    },
    sendPresence(event) {
      updateLivePresence({ live_id: this.video.id, event }).then(response => {
        if (!this.disposed) this.applyLiveHeat(response.data.heat)
      }).catch(error => {
        if (this.disposed) return
        if (error.responseData && Number(error.responseData.code) === 410 && event !== "leave") {
          this.$emit("ended")
        }
      })
    },
    handlePresenceVisibility() {
      if (document.hidden) {
        this.stopPresence(true)
        this.stopRoomRealtimeFallback()
        this.disconnectRoomRealtime(false)
        return
      }
      const player = this.$refs.player
      if (player && !player.paused && !this.loading && !this.failed) this.startPresence()
      this.syncRoomRealtimeSignals().finally(() => this.connectRoomRealtime())
    },
    loadShop() {
      if (!this.currentUser) return
      getLiveGiftCatalog({ live_id: this.video.id }).then(response => {
        this.gifts = response.data.gifts || []
        this.walletBalance = response.data.wallet ? Number(response.data.wallet.balance || 0) : 0
        this.selectedGift = this.gifts[0] || null
      }).catch(() => {})
    },
    openGifts() {
      if (!this.currentUser) {
        this.$emit("login")
        return
      }
      this.closeGame()
      if (!this.gifts.length) this.loadShop()
      this.giftPanelVisible = true
    },
    openRecharge() {
      if (!this.currentUser) {
        this.$emit("login")
        return
      }
      this.rechargeVisible = true
    },
    handleRechargeSuccess(payload) {
      this.walletBalance = Number(payload.balance || 0)
      this.rechargeVisible = false
      this.$emit("recharged", payload)
    },
    requestNo() {
      const randomPart = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID().replace(/-/g, "")
        : `${Date.now()}${Math.random().toString(16).slice(2)}`
      return `LIVEGIFT${randomPart}`.slice(0, 64)
    },
    likeRequestNo() {
      const randomPart = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID().replace(/-/g, "")
        : `${Date.now()}${Math.random().toString(16).slice(2)}`
      return `LIVELIKE${randomPart}`.slice(0, 64)
    },
    isLiveInteractiveTarget(target) {
      if (!(target instanceof Element)) return true
      const interactiveSelector = [
        "button", "a", "input", "textarea", "select", "label",
        "[contenteditable='true']", "[role='button']",
        ".live-room-author", ".live-chat-panel", ".live-chat-editor",
        ".live-quality-sheet", ".live-gift-sheet", ".live-game-sheet", ".live-viewer-sheet", ".live-user-sheet", ".live-viewer-trigger"
      ].join(",")
      return Boolean(target.closest(interactiveSelector))
    },
    handleRoomDoubleClick(event) {
      if (!event || this.loading || this.failed) return
      if (Date.now() < this.suppressRoomClickUntil) return
      if (this.isLiveInteractiveTarget(event.target)) return

      const roomRect = this.$el.getBoundingClientRect()
      this.tapLiveLike({
        x: Math.max(24, Math.min(roomRect.width - 24, event.clientX - roomRect.left)),
        y: Math.max(24, Math.min(roomRect.height - 24, event.clientY - roomRect.top))
      })
    },
    /**
     * 仅从直播画面的非交互区域开始识别纵向切换。
     * 聊天滚动、礼物连击、播放器控制和作者入口均保持原交互。
     */
    handleLiveSwipeStart(event) {
      const touches = event && event.touches
      const touch = touches && touches[0]
      if (!touch || touches.length !== 1 || this.isLiveInteractiveTarget(event.target)) {
        this.resetLiveSwipe()
        return
      }
      this.swipeTracking = true
      this.swipeStartX = touch.clientX
      this.swipeStartY = touch.clientY
      this.swipeLastY = touch.clientY
      this.swipeStartTime = Date.now()
      this.swipeMoved = false
    },
    handleLiveSwipeMove(event) {
      if (!this.swipeTracking || !event.touches || event.touches.length !== 1) return
      const touch = event.touches[0]
      const deltaX = touch.clientX - this.swipeStartX
      const deltaY = touch.clientY - this.swipeStartY
      this.swipeLastY = touch.clientY
      if (Math.abs(deltaY) > 12 && Math.abs(deltaY) > Math.abs(deltaX) * 1.1) {
        this.swipeMoved = true
        this.suppressRoomClickUntil = Date.now() + 450
      }
    },
    handleLiveSwipeEnd(event) {
      if (!this.swipeTracking) return
      const touch = event && event.changedTouches && event.changedTouches[0]
      const endX = touch ? touch.clientX : this.swipeStartX
      const endY = touch ? touch.clientY : this.swipeLastY
      const deltaX = endX - this.swipeStartX
      const deltaY = endY - this.swipeStartY
      const duration = Math.max(1, Date.now() - this.swipeStartTime)
      const velocity = Math.abs(deltaY) / duration
      const isVertical = Math.abs(deltaY) > Math.abs(deltaX) * 1.2
      const reachedThreshold =
        Math.abs(deltaY) >= 58 || (Math.abs(deltaY) >= 32 && velocity >= 0.45)
      if (isVertical && reachedThreshold) {
        this.suppressRoomClickUntil = Date.now() + 500
        this.resetLiveTap()
        // 上滑进入下一场，下滑返回上一场。
        this.$emit("switch-live", deltaY < 0 ? 1 : -1)
      } else if (!this.swipeMoved && Math.hypot(deltaX, deltaY) <= 12) {
        this.handleLiveTouchTap(endX, endY)
      }
      this.resetLiveSwipe()
    },
    handleLiveTouchTap(clientX, clientY) {
      if (this.loading || this.failed) return
      const now = Date.now()
      const interval = now - this.lastLiveTapTime
      const distance = Math.hypot(clientX - this.lastLiveTapX, clientY - this.lastLiveTapY)
      if (interval > 0 && interval <= 320 && distance <= 48) {
        const roomRect = this.$el.getBoundingClientRect()
        this.suppressRoomClickUntil = now + 420
        this.resetLiveTap()
        this.tapLiveLike({
          x: Math.max(24, Math.min(roomRect.width - 24, clientX - roomRect.left)),
          y: Math.max(24, Math.min(roomRect.height - 24, clientY - roomRect.top))
        })
        return
      }
      this.lastLiveTapTime = now
      this.lastLiveTapX = clientX
      this.lastLiveTapY = clientY
    },
    resetLiveTap() {
      this.lastLiveTapTime = 0
      this.lastLiveTapX = 0
      this.lastLiveTapY = 0
    },
    resetLiveSwipe() {
      this.swipeTracking = false
      this.swipeStartX = 0
      this.swipeStartY = 0
      this.swipeLastY = 0
      this.swipeStartTime = 0
      this.swipeMoved = false
    },
    tapLiveLike(position = null) {
      if (!this.currentUser) {
        this.$emit("login")
        return
      }
      this.pendingLikeQuantity += 1
      if (position && Number.isFinite(position.x) && Number.isFinite(position.y)) {
        this.spawnScreenLike(position)
      } else {
        this.spawnLikeBursts(1)
      }
      window.clearTimeout(this.likeSendTimer)
      if (this.pendingLikeQuantity >= 99) {
        this.flushLiveLikes()
        return
      }
      this.likeSendTimer = window.setTimeout(this.flushLiveLikes, 320)
    },
    flushLiveLikes() {
      window.clearTimeout(this.likeSendTimer)
      this.likeSendTimer = null
      if (this.disposed || this.likeSending || this.pendingLikeQuantity <= 0) return
      const quantity = Math.min(99, this.pendingLikeQuantity)
      this.pendingLikeQuantity -= quantity
      this.activeLikeQuantity = quantity
      this.likeSending = true
      addLiveLikes({
        live_id: this.video.id,
        quantity,
        client_request_no: this.likeRequestNo()
      }).then(response => {
        if (this.disposed) return
        this.liveLikeCount = Number(response.data.total || this.liveLikeCount)
        this.pollLiveLikes()
      }).catch(error => {
        if (error.responseData && Number(error.responseData.code) === 410) {
          this.$emit("ended")
        } else if (error.responseData && Number(error.responseData.code) === 401) {
          this.$emit("login")
        } else {
          this.$notice.error(error.message)
        }
      }).finally(() => {
        this.activeLikeQuantity = 0
        this.likeSending = false
        if (!this.disposed && this.pendingLikeQuantity > 0) {
          this.likeSendTimer = window.setTimeout(this.flushLiveLikes, 120)
        }
      })
    },
    spawnLikeBursts(count) {
      const visibleCount = Math.min(5, Math.max(1, Number(count || 1)))
      for (let index = 0; index < visibleCount; index += 1) {
        this.likeBurstSequence += 1
        const heart = {
          id: this.likeBurstSequence,
          drift: Math.round((Math.random() - 0.5) * 32),
          rotate: Math.round((Math.random() - 0.5) * 28)
        }
        this.likeBursts.push(heart)
        const timer = window.setTimeout(() => {
          this.likeBursts = this.likeBursts.filter(item => item.id !== heart.id)
          this.likeBurstTimers.delete(heart.id)
        }, 1050)
        this.likeBurstTimers.set(heart.id, timer)
      }
      this.likeBursts = this.likeBursts.slice(-18)
    },
    spawnScreenLike(position) {
      this.screenLikeSequence += 1
      const heart = {
        id: this.screenLikeSequence,
        x: position.x,
        y: position.y,
        rotate: Math.round((Math.random() - 0.5) * 26)
      }
      this.screenLikeBursts.push(heart)
      const timer = window.setTimeout(() => {
        this.screenLikeBursts = this.screenLikeBursts.filter(item => item.id !== heart.id)
        this.screenLikeTimers.delete(heart.id)
      }, 760)
      this.screenLikeTimers.set(heart.id, timer)
      this.screenLikeBursts = this.screenLikeBursts.slice(-12)
    },
    async initializeLiveLikes() {
      window.clearTimeout(this.likePollTimer)
      try {
        const response = await getLiveLikes({
          live_id: this.video.id,
          after_id: 0,
          initialize: 1
        })
        if (this.disposed) return
        this.lastLikeId = Number(response.data.last_id || 0)
        this.liveLikeCount = Number(response.data.total || 0)
        this.applyLiveHeat(response.data.heat)
        this.likePollInitialized = true
      } catch (error) {
        if (error.responseData && Number(error.responseData.code) === 410) this.$emit("ended")
      }
    },
    pollLiveLikes() {
      if (this.disposed || this.likePolling || !this.likePollInitialized) return
      window.clearTimeout(this.likePollTimer)
      this.likePolling = true
      return getLiveLikes({
        live_id: this.video.id,
        after_id: this.lastLikeId,
        initialize: 0
      }).then(response => {
        const list = response.data.list || []
        this.lastLikeId = Number(response.data.last_id || this.lastLikeId)
        this.liveLikeCount = Number(response.data.total || this.liveLikeCount)
        this.applyLiveHeat(response.data.heat)
        list.filter(event => !event.is_owner).forEach(event => {
          this.spawnLikeBursts(Math.min(3, Number(event.quantity || 1)))
        })
        return list.length
      }).catch(error => {
        if (error.responseData && Number(error.responseData.code) === 410) this.$emit("ended")
      }).finally(() => {
        this.likePolling = false
      })
    },
    selectGift(gift) {
      if (!gift) return
      this.selectedGift = gift
    },
    isGiftSending(gift) {
      return Boolean(
        gift
        && this.giftSending
        && this.activeGiftBatch
        && Number(this.activeGiftBatch.giftId) === Number(gift.id)
      )
    },
    queueGift(gift = this.selectedGift) {
      if (!gift) return
      this.selectedGift = gift
      const unitPrice = Number(gift.coin_price || 0)
      const requestedQuantity = Math.min(99, Math.max(1, Number(this.quantity || 1)))
      const requestedCoins = unitPrice * requestedQuantity
      if (unitPrice <= 0 || requestedCoins > this.spendableGiftBalance) {
        this.$notice.error(this.$t("errors.WALLET.INSUFFICIENT_BALANCE"))
        return
      }

      let remaining = requestedQuantity
      while (remaining > 0) {
        const last = this.giftSendQueue[this.giftSendQueue.length - 1]
        const canMerge = last
          && Number(last.giftId) === Number(gift.id)
          && Number(last.quantity) < 99
        if (canMerge) {
          const addition = Math.min(remaining, 99 - Number(last.quantity))
          last.quantity += addition
          last.coinAmount += unitPrice * addition
          remaining -= addition
          continue
        }
        const batchQuantity = Math.min(remaining, 99)
        this.giftSendQueue.push({
          giftId: Number(gift.id),
          giftName: gift.name,
          quantity: batchQuantity,
          coinAmount: unitPrice * batchQuantity,
          requestNo: this.requestNo()
        })
        remaining -= batchQuantity
      }

      this.comboCount += requestedQuantity
      window.clearTimeout(this.comboTimer)
      this.comboTimer = window.setTimeout(() => { this.comboCount = 0 }, 1600)
      window.clearTimeout(this.giftQueueTimer)
      this.giftQueueTimer = window.setTimeout(this.processGiftQueue, 160)
    },
    processGiftQueue() {
      if (this.disposed || this.giftSending || !this.giftSendQueue.length) return
      window.clearTimeout(this.giftQueueTimer)
      this.giftQueueTimer = null
      const batch = this.giftSendQueue.shift()
      this.activeGiftBatch = batch
      this.giftSending = true
      addGift({
        author_id: this.video.author.id,
        live_id: this.video.id,
        gift_id: batch.giftId,
        quantity: batch.quantity,
        client_request_no: batch.requestNo,
        confirm: 1
      }).then(response => {
        if (this.disposed) return
        this.walletBalance = Number(response.data.balance || 0)
        this.$emit("gift-sent")
        this.pollGifts()
      }).catch(() => {
        // 失败时清空尚未扣款的队列，避免余额不足或直播结束后继续请求。
        this.giftSendQueue = []
        this.comboCount = 0
      }).finally(() => {
        this.activeGiftBatch = null
        this.giftSending = false
        if (!this.disposed && this.giftSendQueue.length) {
          this.giftQueueTimer = window.setTimeout(this.processGiftQueue, 90)
        }
      })
    },
    enqueueGiftEvents(list) {
      list.forEach(event => {
        const eventId = String(event.id)
        if (this.giftEvents.some(item => String(item.id) === eventId)) return
        this.giftEvents.push(event)
        const timer = window.setTimeout(() => {
          this.giftEvents = this.giftEvents.filter(item => String(item.id) !== eventId)
          this.giftDismissTimers.delete(eventId)
        }, GIFT_DISPLAY_MS)
        this.giftDismissTimers.set(eventId, timer)
      })
      this.giftEvents = this.giftEvents.slice(-12)
    },
    enqueueEntranceEvents(list) {
      list.forEach(event => {
        // 没有真实 PNG 入场特效时不显示空容器或平台默认入场。
        if (!String(event.preview_url || "").trim()) return
        const eventId = String(event.id)
        if (this.entranceEvents.some(item => String(item.id) === eventId)) return
        this.entranceEvents.push(event)
        const timer = window.setTimeout(() => {
          this.entranceEvents = this.entranceEvents.filter(item => String(item.id) !== eventId)
          this.entranceDismissTimers.delete(eventId)
        }, ENTRANCE_DISPLAY_MS)
        this.entranceDismissTimers.set(eventId, timer)
      })
      this.entranceEvents = this.entranceEvents.slice(-8)
    },
    async initializeEntrances() {
      // 本人入场不等待历史游标请求；服务端返回匹配权益后直接在本机播放一次。
      this.announceOwnEntrance()
      try {
        const response = await getLiveEntrances({
          live_id: this.video.id,
          after_id: 0,
          initialize: 1
        })
        this.lastEntranceId = Number(response.data.last_id || 0)
        this.entrancePollInitialized = true
      } catch (error) {
        if (error.responseData && Number(error.responseData.code) === 410) this.$emit("ended")
      }

      if (this.disposed) return
    },
    async announceOwnEntrance() {
      if (this.disposed || !this.currentUser || this.ownEntranceAnnounced) return
      this.ownEntranceAnnounced = true
      try {
        const response = await addLiveEntrance({ live_id: this.video.id })
        if (this.disposed) return
        if (response.data.event) {
          // 不依赖轮询回显，本人端收到响应后立即展示；事件 ID 仍用于防止后续轮询重复播放。
          this.enqueueEntranceEvents([response.data.event])
        }
      } catch (error) {
        this.ownEntranceAnnounced = false
        if (error.responseData && Number(error.responseData.code) === 410) this.$emit("ended")
      }
    },
    pollEntrances() {
      if (this.disposed || this.entrancePolling || !this.entrancePollInitialized) return
      window.clearTimeout(this.entrancePollTimer)
      this.entrancePolling = true
      return getLiveEntrances({
        live_id: this.video.id,
        after_id: this.lastEntranceId,
        initialize: 0
      }).then(response => {
        const list = response.data.list || []
        this.lastEntranceId = Number(response.data.last_id || this.lastEntranceId)
        if (list.length) this.enqueueEntranceEvents(list)
        return list.length
      }).catch(error => {
        if (error.responseData && Number(error.responseData.code) === 410) this.$emit("ended")
      }).finally(() => {
        this.entrancePolling = false
      })
    },
    pollGifts() {
      if (this.disposed || this.giftPolling) return
      window.clearTimeout(this.pollTimer)
      const initialize = !this.giftPollInitialized
      this.giftPolling = true
      return getLiveGifts({
        live_id: this.video.id,
        after_id: this.lastGiftId,
        initialize: initialize ? 1 : 0
      }).then(response => {
        const list = response.data.list || []
        this.lastGiftId = Number(response.data.last_id || this.lastGiftId)
        if (initialize) this.giftPollInitialized = true
        else if (list.length) this.enqueueGiftEvents(list)
        return list.length
      }).catch(error => {
        if (error.responseData && Number(error.responseData.code) === 410) this.$emit("ended")
      }).finally(() => {
        this.giftPolling = false
      })
    },
    enqueueChatMessages(list) {
      list.forEach(message => {
        const clientMessageId = String(message.client_message_id || "")
        const existingIndex = this.chatMessages.findIndex(item =>
          (clientMessageId && String(item.client_message_id || "") === clientMessageId)
          || (Number(message.id) > 0 && Number(item.id) === Number(message.id))
        )
        if (existingIndex >= 0) this.$set(this.chatMessages, existingIndex, { ...message, delivery_state: "sent" })
        else this.chatMessages.push({ ...message, delivery_state: "sent" })
      })
      this.chatMessages = this.chatMessages.slice(-60)
      this.$nextTick(this.scrollChatToBottom)
    },
    scrollChatToBottom() {
      const list = this.$refs.chatList
      if (list) list.scrollTop = list.scrollHeight
    },
    async initializeChat() {
      window.clearTimeout(this.chatTimer)
      this.chatTimer = null
      this.chatMessages = []
      this.lastChatId = 0
      this.chatInitialized = false
      if (this.disposed) return
      try {
        const response = await getLiveChat({ live_id: this.video.id, after_id: 0, limit: 30 })
        if (this.disposed) return
        this.chatMessages = response.data.list || []
        this.lastChatId = Number(response.data.last_id || 0)
        this.chatInitialized = true
        this.$nextTick(this.scrollChatToBottom)
      } catch (error) {
        if (error.responseData && Number(error.responseData.code) === 410) this.$emit("ended")
      }
    },
    pollChat() {
      if (this.disposed || this.chatPolling || !this.chatInitialized) return
      window.clearTimeout(this.chatTimer)
      this.chatPolling = true
      return getLiveChat({ live_id: this.video.id, after_id: this.lastChatId, limit: 50 }).then(response => {
        const list = response.data.list || []
        this.lastChatId = Number(response.data.last_id || this.lastChatId)
        if (list.length) this.enqueueChatMessages(list)
        return list.length
      }).catch(error => {
        if (error.responseData && Number(error.responseData.code) === 410) this.$emit("ended")
      }).finally(() => {
        this.chatPolling = false
      })
    },
    handleChatEnter(event) {
      if (event && event.isComposing) return
      this.sendChat()
    },
    chatRequestId() {
      const randomPart = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID().replace(/-/g, "")
        : `${Date.now()}${Math.random().toString(16).slice(2)}`
      return `CHAT${randomPart}`.slice(0, 64)
    },
    optimisticChatMessage(content, clientMessageId) {
      return {
        id: `local-${clientMessageId}`,
        client_message_id: clientMessageId,
        content,
        create_time: new Date().toISOString(),
        user_id: Number(this.currentUser.id),
        user_name: this.currentUser.name || "",
        avatar_url: this.currentUser.avatar_url || "",
        avatar_frame_url: this.currentUser.avatar_frame_url || "",
        avatar_frame_preview_url: this.currentUser.avatar_frame_preview_url || "",
        level: Number(this.currentUser.level || 0),
        level_title: this.currentUser.level_title || "",
        level_icon_url: this.currentUser.level_icon_url || "",
        is_owner: true,
        is_self: true,
        delivery_state: "sending"
      }
    },
    async dispatchChatMessage(message) {
      message.delivery_state = "sending"
      try {
        const response = await addLiveChat({ live_id: this.video.id, client_message_id: message.client_message_id, content: message.content })
        if (response.data.message) this.enqueueChatMessages([response.data.message])
      } catch (error) {
        message.delivery_state = "failed"
        if (error.responseData && Number(error.responseData.code) === 410) this.$emit("ended")
      }
    },
    retryChat(message) {
      if (!message || message.delivery_state !== "failed") return
      this.dispatchChatMessage(message)
    },
    sendChat() {
      if (!this.currentUser) {
        this.$emit("login")
        return
      }
      const content = this.chatText.trim()
      if (!content) return
      const message = this.optimisticChatMessage(content, this.chatRequestId())
      this.chatMessages.push(message)
      this.chatText = ""
      this.mentionVisible = false
      this.scrollChatToBottom()
      this.dispatchChatMessage(message)
    }
  }
}
</script>

<style scoped>
.live-chat-list article.sending>p{opacity:.82}.live-chat-list article.failed>p{outline:1px solid rgba(255,196,75,.85)}.live-chat-retry{display:inline-grid!important;width:18px!important;height:18px!important;margin-inline-start:5px!important;padding:0!important;border:0!important;border-radius:50%!important;background:rgba(0,0,0,.18)!important;color:#fff!important;font-size:11px!important;vertical-align:middle}
</style>
